// src/pages/Login.jsx
import { useState, useEffect, useRef } from 'react';
import { trackPage } from '../utils/visits';
import { useAuth } from '../hooks/useAuth';
import AuthShell, { A, AuthMessage, AuthButton, PasswordField } from '../components/AuthShell';
import { fetchRememberAvailable } from '../utils/api';
import { unlockWord, isInstalledApp, passkeyReady, passkeySignInPossible } from '../utils/passkeys';
import Emo from '../components/Emo';

export default function Login() {
  useEffect(() => { trackPage('signin'); }, []);   // 3.49.0: Analytics (visitors before signing in)
  const { loginWithGoogle, loginWithMicrosoft, loginWithEmail, forgotPassword, error, setError, errorField, errorTone,
          mfaChallenge, completeMfaSignIn, cancelMfaSignIn,
          recoverWithCode, emailTwoFactorResetLink, backupCodeOptions, sendBackupCode, signInWithBackupCode, listTwoFactorApprovers, askAdminForTwoFactorReset,
          lock, unlockWithPasskey, leaveLock, deviceSignIn, loginWithPasskey } = useAuth();
  const emailRef    = useRef(null);
  const passwordRef = useRef(null);
  const codeRef     = useRef(null);

  // Put the cursor in the field that needs fixing
  useEffect(() => {
    const ref = { email: emailRef, password: passwordRef, code: codeRef }[errorField];
    ref?.current?.focus();
    if (errorField === 'password' || errorField === 'code') ref?.current?.select?.();
  }, [error, errorField]);

  // Field styling and screen-reader flag for the field with a problem
  const fieldProps = name => errorField === name
    ? { 'aria-invalid': true, 'aria-describedby': 'signin-error' }
    : {};
  const fieldStyle = (base, name) => (errorField === name ? { ...base, ...A.inputError } : base);
  // 3.40.0: messages with an icon; errors give a small shake
  const messageBox = error && <AuthMessage id="signin-error" tone={errorTone === 'info' ? 'info' : 'error'}>{error}</AuthMessage>;
  const [mfaCode, setMfaCode]   = useState('');
  // "Don't ask for a code on this device for 30 days" (when the server can)
  const [remember, setRemember] = useState(true);
  const [rememberDays, setRememberDays] = useState(0);
  const onCodeScreen = !!mfaChallenge;
  // 3.85.0: a code by email or WhatsApp (lostView 'backup')
  const [backupCode, setBackupCode] = useState('');
  const [codeChannel, setCodeChannel] = useState('email');
  const [waEnding, setWaEnding] = useState(null);
  useEffect(() => {
    if (!onCodeScreen || rememberDays) return;
    fetchRememberAvailable().then(r => { if (r.available) setRememberDays(r.days || 30); }).catch(() => {});
  }, [onCodeScreen, rememberDays]);
  // "Lost access to your authenticator app?": null | 'menu' | 'code'
  const [lostView, setLostView] = useState(null);   // also 'ask': choose who to ask
  const [approvers, setApprovers] = useState(null);  // [{ id, name, roleLabel, alreadyAsked }]
  const [picked, setPicked]       = useState([]);
  const [lostMsg, setLostMsg]   = useState(null);   // { tone: 'error'|'info'|'success', text, useAdmin? }
  const [recoveryCode, setRecoveryCode] = useState('');
  const [mode, setMode]         = useState('login'); // login | forgot (no account creation here since 3.20.0)
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading]   = useState(false);
  const [success, setSuccess]   = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (mode === 'login') {
      await loginWithEmail(email, password);
    } else if (mode === 'forgot') {
      const ok = await forgotPassword(email);
      if (ok) setSuccess('Password reset email sent. Check your inbox.');
    }
    setLoading(false);
  }

  async function handleGoogle() {
    setLoading(true);
    setError('');
    await loginWithGoogle();
    setLoading(false);
  }

  // 3.72.0: Microsoft with the same email as the account, or one connected in Security (3.72.1)
  // 3.74.4: any device, no email or code (synced passkeys or a phone by QR)
  async function handlePasskey() {
    setLoading(true);
    setError('');
    await loginWithPasskey();
    setLoading(false);
  }

  async function handleMicrosoft() {
    setLoading(true);
    setError('');
    await loginWithMicrosoft();
    setLoading(false);
  }

  // Two-factor sign-in: the password or Google step passed, now the code
  async function handleMfa(e) {
    e.preventDefault();
    // Say what's wrong instead of a button that does nothing
    if (!mfaCode) { setError('Enter the 6-digit code from your authenticator app.', 'code'); codeRef.current?.focus(); return; }
    if (mfaCode.length < 6) {
      setError(`The code has 6 digits. You entered ${mfaCode.length}.`, 'code');
      codeRef.current?.focus();
      return;
    }
    setLoading(true);
    const ok = await completeMfaSignIn(mfaCode, !!rememberDays && remember && mfaChallenge?.loginMethod !== 'Microsoft');
    if (!ok) setMfaCode('');
    setLoading(false);
  }

  function leaveMfa() {
    cancelMfaSignIn();
    setMfaCode(''); setLostView(null); setLostMsg(null); setRecoveryCode('');
  }

  function openLost(view) {
    setError('');
    setLostMsg(null);
    setLostView(view);
  }

  async function submitRecoveryCode(e) {
    e.preventDefault();
    const typed = recoveryCode.replace(/-/g, '').length;
    if (typed !== 10) {
      setLostMsg({ tone: 'error', text: typed ? `A recovery code has 10 letters and numbers, like K7PQM-3XWRD. You entered ${typed}.` : 'Enter one of your recovery codes, like K7PQM-3XWRD.' });
      return;
    }
    setLoading(true); setLostMsg(null);
    try {
      const outcome = await recoverWithCode(recoveryCode);
      setLostView(null); setRecoveryCode('');
      if (outcome === 'sign-in-again') {
        setError('Your recovery code worked and your old authenticator was removed. Sign in again to set up a new one.', null, 'info');
      }
    } catch (e2) {
      setLostMsg({ tone: 'error', text: e2.message });
    }
    setLoading(false);
  }

  // 3.85.0: a one-time code by email, or WhatsApp if they've confirmed a number
  async function sendCode(channel) {
    setLoading(true); setLostMsg(null);
    try {
      const r = await sendBackupCode(channel);
      setCodeChannel(channel); setBackupCode('');
      setLostView('backup');
      setLostMsg({ tone: 'success', text: `We've sent a code to ${r.sentTo}.` });
    } catch (e2) {
      setLostMsg({ tone: 'error', text: e2.message, useAdmin: e2.code === 'USE_ADMIN' });
    }
    setLoading(false);
  }

  async function submitBackupCode(e) {
    e.preventDefault();
    if (backupCode.length !== 6) { setLostMsg({ tone: 'error', text: 'Enter the 6-digit code we sent.' }); return; }
    setLoading(true); setLostMsg(null);
    try {
      await signInWithBackupCode(backupCode);
      setLostView(null); setBackupCode('');
    } catch (e2) {
      setLostMsg({ tone: 'error', text: e2.message });
      setLoading(false);
    }
  }

  async function emailLink() {
    setLoading(true); setLostMsg(null);
    try {
      const r = await emailTwoFactorResetLink();
      setLostMsg({ tone: 'success', text: `We've emailed a reset link to ${r.sentTo}. It works once and expires in 30 minutes. If you don't see it, check your spam folder.` });
    } catch (e2) {
      setLostMsg({ tone: 'error', text: e2.message, useAdmin: e2.code === 'USE_ADMIN' });
    }
    setLoading(false);
  }

  // "Ask someone to reset it": list who can, then ask the ones picked
  async function openAsk() {
    setLoading(true); setLostMsg(null);
    try {
      const r = await listTwoFactorApprovers();
      setApprovers(r.approvers || []);
      setPicked([]);
      setLostView('ask');
    } catch (e2) {
      setLostMsg({ tone: 'error', text: e2.message });
    }
    setLoading(false);
  }

  const joinNames = names => names.length <= 1 ? (names[0] || '')
    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

  async function askAdmin(e) {
    e.preventDefault();
    setLoading(true); setLostMsg(null);
    try {
      const r = await askAdminForTwoFactorReset(picked);
      const who = joinNames(r.askedNames || []);
      setLostMsg({ tone: 'success', text: r.alreadyAsked
        ? `You already asked ${who} in the last 24 hours. You'll get an email once it's reset.`
        : `We've asked ${who} to reset two-factor authentication. They may contact you to confirm it's you. You'll get an email once it's reset.` });
      setApprovers(list => list.map(a => (picked.includes(a.id) ? { ...a, alreadyAsked: true } : a)));
      setPicked([]);
    } catch (e2) {
      setLostMsg({ tone: 'error', text: e2.message });
    }
    setLoading(false);
  }

  useEffect(() => {
    if (!mfaChallenge || lostView !== 'menu') return undefined;
    let live = true;
    backupCodeOptions().then(r => { if (live) setWaEnding(r.whatsappEnding || null); }).catch(() => { if (live) setWaEnding(null); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lostView, !!mfaChallenge]);

  const lostBox = lostMsg && <AuthMessage tone={lostMsg.tone}>{lostMsg.text}</AuthMessage>;

  // 3.69.0: locked after an hour without use, on a device with Face ID /
  // fingerprint unlock (3.69.2: or signed out there). "Use password
  // instead" keeps their email; "Use another account" doesn't.
  if (lock && !mfaChallenge) {
    return <LockScreen lock={lock} unlock={unlockWithPasskey} messageBox={messageBox}
      onPassword={() => { setEmail(lock.email); leaveLock(); }}
      onOther={() => { setEmail(''); setPassword(''); leaveLock(); }} />;
  }
  // 3.69.2: the typed email has it on this device
  const deviceReady = mode === 'login' && /@/.test(email) && passkeyReady(email);

  if (mfaChallenge && lostView === 'backup') {
    return (
      <AuthShell viewKey="backup-code">
        <h1 style={A.title}>Enter your code</h1>
        <p style={A.subtitle}>Enter the code we sent. It expires in 10 minutes.</p>
        <form onSubmit={submitBackupCode} style={A.form} noValidate>
          <label style={A.label} htmlFor="backup-code">Code</label>
          <input id="backup-code" value={backupCode} autoFocus
            onChange={e => setBackupCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric" autoComplete="one-time-code" placeholder="123456"
            style={{ ...A.input, fontSize: 26, letterSpacing: '10px', textAlign: 'center', fontFamily: 'monospace', padding: '12px 10px' }} />
          {lostBox}
          <AuthButton busy={loading} busyLabel="Checking…">Sign in</AuthButton>
        </form>
        <div style={A.links}>
          <button onClick={() => sendCode(codeChannel)} disabled={loading} style={A.link} className="auth-link">Send a new code</button>
          <span style={A.linkSep}>·</span>
          <button onClick={() => openLost('menu')} style={A.link} className="auth-link">← Other options</button>
        </div>
      </AuthShell>
    );
  }

  if (mfaChallenge && lostView === 'code') {
    return (
      <AuthShell viewKey="recovery">
        <h1 style={A.title}>Use a recovery code</h1>
        <p style={A.subtitle}>Enter one of the recovery codes you saved when you set up two-factor authentication. Each code works once.</p>
        <form onSubmit={submitRecoveryCode} style={A.form} noValidate>
          <label style={A.label} htmlFor="recovery-code">Recovery code</label>
          <input id="recovery-code" value={recoveryCode} autoFocus
            onChange={e => setRecoveryCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 11))}
            autoComplete="off" spellCheck={false} placeholder="XXXXX-XXXXX"
            style={{ ...A.input, fontSize: 19, letterSpacing: '3px', textAlign: 'center', fontFamily: 'monospace' }} />
          {lostBox}
          <AuthButton busy={loading} busyLabel="Checking…">Use recovery code</AuthButton>
        </form>
        <p style={A.smallNote}>This removes your old authenticator. You'll set up a new one straight after.</p>
        <div style={A.links}>
          <button onClick={() => openLost('menu')} style={A.link} className="auth-link">← Other options</button>
        </div>
      </AuthShell>
    );
  }

  if (mfaChallenge && lostView === 'ask') {
    return (
      <AuthShell viewKey="ask">
        <h1 style={A.title}>Who should reset it?</h1>
        <p style={A.subtitle}>Choose one or more people. We'll email them your request. They may contact you to confirm it's you.</p>
        <form onSubmit={askAdmin} style={A.form}>
          <fieldset style={S.pickList}>
            <legend style={S.srOnly}>People who can reset your two-factor authentication</legend>
            {(approvers || []).map(a => (
              <label key={a.id} className="auth-option" style={{ ...S.pickRow, ...(picked.includes(a.id) ? S.pickRowOn : {}) }}>
                <input type="checkbox" checked={picked.includes(a.id)}
                  onChange={e => setPicked(p => (e.target.checked ? [...p, a.id] : p.filter(x => x !== a.id)))} />
                <span style={S.pickName}>{a.name}</span>
                <span style={S.pickRole}>{a.roleLabel}{a.alreadyAsked ? ' · asked today' : ''}</span>
              </label>
            ))}
            {approvers && !approvers.length && (
              <p style={A.smallNote}>Nobody can reset it for you right now. Please contact support@crazypixels.org.</p>
            )}
          </fieldset>
          {lostBox}
          <AuthButton busy={loading} busyLabel="Sending…" disabled={!picked.length}>
            {picked.length > 1 ? `Ask these ${picked.length} people` : 'Send request'}
          </AuthButton>
        </form>
        <div style={A.links}>
          <button onClick={() => openLost('menu')} style={A.link} className="auth-link">← Other options</button>
        </div>
      </AuthShell>
    );
  }

  if (mfaChallenge && lostView === 'menu') {
    const email = mfaChallenge.proof?.email || '';
    return (
      <AuthShell viewKey="lost">
        <h1 style={A.title}>Lost access to your authenticator app?</h1>
        <p style={A.subtitle}>Choose one of these to get back in.</p>
        <div style={S.options}>
          <button onClick={() => sendCode('email')} disabled={loading} style={S.option} className="auth-option">
            <span style={S.optionTitle}><Emo e="✉️" /> Email me a code</span>
            <span style={S.optionText}>We'll email a one-time code{email ? ` to ${email}` : ''}. Your authenticator stays as it is.</span>
          </button>
          {waEnding && (
            <button onClick={() => sendCode('whatsapp')} disabled={loading} style={S.option} className="auth-option">
              <span style={S.optionTitle}><Emo e="💬" /> Send a code to WhatsApp ending {waEnding}</span>
              <span style={S.optionText}>Your authenticator stays as it is.</span>
            </button>
          )}
          <button onClick={() => openLost('code')} disabled={loading} style={S.option} className="auth-option">
            <span style={S.optionTitle}><Emo e="🔑" /> Use a recovery code</span>
            <span style={S.optionText}>One of the 10 codes you saved when you set up two-factor authentication.</span>
          </button>
          <button onClick={emailLink} disabled={loading} style={S.option} className="auth-option">
            <span style={S.optionTitle}><Emo e="✉️" /> Email me a reset link</span>
            <span style={S.optionText}>We'll email a link{email ? ` to ${email}` : ''} that removes your old authenticator. It works once, for 30 minutes.</span>
          </button>
          <button onClick={openAsk} disabled={loading} className="auth-option"
            style={lostMsg?.useAdmin ? { ...S.option, ...S.optionHighlight } : S.option}>
            <span style={S.optionTitle}><Emo e="🛟" /> Ask an administrator</span>
            <span style={S.optionText}>Choose who to ask from your team's Managers and Admins. They may contact you to confirm it's you.</span>
          </button>
        </div>
        {loading && <p style={A.smallNote}>Please wait…</p>}
        {lostBox}
        <div style={A.links}>
          <button onClick={() => { setLostView(null); setLostMsg(null); }} style={A.link} className="auth-link">← Back to the code screen</button>
          <span style={A.linkSep}>·</span>
          <button onClick={leaveMfa} style={A.link} className="auth-link">Back to sign in</button>
        </div>
      </AuthShell>
    );
  }

  if (mfaChallenge) {
    return (
      <AuthShell viewKey="mfa">
        <div style={S.badgeIcon} aria-hidden="true"><Emo e="🔐" /></div>
        <h1 style={A.title}>Two-factor sign-in</h1>
        <p style={A.subtitle}>Enter the 6-digit code from your authenticator app.</p>
        <form onSubmit={handleMfa} style={A.form} noValidate>
          <label style={A.label} htmlFor="mfa-code">Code</label>
          <input id="mfa-code" ref={codeRef} {...fieldProps('code')} value={mfaCode} autoFocus
            onChange={e => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric" autoComplete="one-time-code" placeholder="123456"
            style={fieldStyle({ ...A.input, fontSize: 26, letterSpacing: '10px', textAlign: 'center', fontFamily: 'monospace', padding: '12px 10px' }, 'code')} />
          <div style={S.codeDots} aria-hidden="true">
            {[0, 1, 2, 3, 4, 5].map(i => <span key={i} style={{ ...S.codeDot, ...(i < mfaCode.length ? S.codeDotOn : {}) }} />)}
          </div>
          {/* 3.72.0: a remembered device needs the password or Google step, so not offered after Microsoft */}
          {rememberDays > 0 && mfaChallenge?.loginMethod !== 'Microsoft' && (
            <label style={S.rememberRow}>
              <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} style={{ marginTop: 2 }} />
              <span>
                Don't ask for a code on this device for {rememberDays} days
                <span style={S.rememberHint}>You'll still sign in with your password. Leave this unticked on a shared computer.</span>
              </span>
            </label>
          )}
          {messageBox}
          <AuthButton busy={loading} busyLabel="Checking…">Verify and sign in</AuthButton>
        </form>
        <div style={A.links}>
          <button onClick={() => openLost('menu')} style={A.link} className="auth-link">Lost access to your authenticator app?</button>
        </div>
        <div style={{ ...A.links, marginTop: 10 }}>
          <button onClick={leaveMfa} style={A.link} className="auth-link">← Back to sign in</button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell viewKey={mode}>
      <h1 style={A.title}>{mode === 'login' ? 'Welcome back' : 'Reset your password'}</h1>
      <p style={A.subtitle}>
        {mode === 'login' ? 'Sign in to manage your faxes' : "Enter your email and we'll send you a link to choose a new password."}
      </p>

      {mode === 'login' && (
        <>
          <button onClick={handleGoogle} disabled={loading} className="auth-google">
            <GoogleIcon />
            <span>Continue with Google</span>
          </button>
          <button onClick={handleMicrosoft} disabled={loading} className="auth-google" style={{ marginTop: 10 }}>
            <MicrosoftIcon />
            <span>Continue with Microsoft</span>
          </button>
          {passkeySignInPossible() && (
            <button onClick={handlePasskey} disabled={loading} className="auth-google" style={{ marginTop: 10 }} title={`Use ${unlockWord()}, or a passkey on your phone`}>
              <PasskeyIcon />
              <span>Sign in with a passkey</span>
            </button>
          )}
          <div className="auth-divider">or sign in with email</div>
        </>
      )}

      <form onSubmit={handleSubmit} style={A.form}>

        <label style={A.label} htmlFor="signin-email">Email address</label>
        <input
          id="signin-email" type="email" value={email} onChange={e => { setEmail(e.target.value); if (errorField === 'email') setError(''); }}
          ref={emailRef} {...fieldProps('email')} autoComplete="email"
          placeholder="you@yourcompany.com" required style={fieldStyle(A.input, 'email')}
        />

        {deviceReady && (
          <>
            <AuthButton type="button" onClick={() => deviceSignIn(email)} busy={false}>Sign in with {unlockWord()}</AuthButton>
            <div className="auth-divider">or use your password</div>
          </>
        )}

        {mode === 'login' && (
          <>
            <div style={S.pwLabelRow}>
              <label style={A.label} htmlFor="signin-password">Password</label>
              <button type="button" onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }} style={{ ...A.link, fontSize: 12.5 }} className="auth-link">
                Forgot password?
              </button>
            </div>
            <PasswordField
              id="signin-password" value={password} onChange={e => { setPassword(e.target.value); if (errorField === 'password') setError(''); }}
              inputRef={passwordRef} {...fieldProps('password')} autoComplete="current-password"
              placeholder="••••••••" required
              style={errorField === 'password' ? A.inputError : undefined}
            />
          </>
        )}

        {messageBox}
        {success && <AuthMessage tone="success">{success}</AuthMessage>}

        <AuthButton busy={loading}>{mode === 'login' ? 'Sign in' : 'Send reset email'}</AuthButton>
      </form>

      {mode === 'forgot' && (
        <div style={A.links}>
          <button onClick={() => { setMode('login'); setError(''); setSuccess(''); }} style={A.link} className="auth-link">
            ← Back to sign in
          </button>
        </div>
      )}
    </AuthShell>
  );
}

// 3.69.0: "Welcome back, Pat" + Unlock with Face ID. The installed app
// starts Face ID by itself (once) after the hour; in a browser, and after
// signing out (3.69.2: "Sign in with Face ID"), it waits for the button.
function LockScreen({ lock, unlock, onPassword, onOther, messageBox }) {
  const [busy, setBusy] = useState(false);
  const word  = unlockWord();
  const first = String(lock.name || '').trim().split(/\s+/)[0];
  const face  = /Face ID/.test(word);
  async function go(quiet = false) {
    if (busy) return;
    setBusy(true);
    const ok = await unlock({ quiet });
    if (!ok) setBusy(false);
  }
  const started = useRef(false);
  useEffect(() => {
    if (started.current || lock.why === 'signout' || !isInstalledApp() || document.visibilityState !== 'visible') return;
    started.current = true;
    go(true);
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <AuthShell viewKey="lock">
      <button type="button" onClick={() => go()} disabled={busy} style={S.unlockIcon} className={busy ? 'lock-scan' : 'lock-idle'} aria-label={`Unlock with ${word}`}>
        {face ? <FaceScanIcon /> : <FingerprintIcon />}
      </button>
      <h1 style={A.title}>{first ? `Welcome back, ${first}` : 'Welcome back'}</h1>
      <p style={A.subtitle}>{lock.why === 'signout' ? 'Sign in again on this device without your portal password.' : 'The portal locked after an hour without use.'}</p>
      {messageBox}
      <AuthButton type="button" onClick={() => go()} busy={busy} busyLabel="Checking it's you…">{lock.why === 'signout' ? 'Sign in' : 'Unlock'} with {word}</AuthButton>
      <p style={{ ...A.smallNote, textAlign: 'center', overflowWrap: 'anywhere' }}>{lock.email}</p>
      <div style={A.links}>
        <button onClick={onPassword} style={A.link} className="auth-link">Use password instead</button>
        <span aria-hidden="true" style={{ color: '#B0BEC5' }}>·</span>
        <button onClick={onOther} style={A.link} className="auth-link">Use another account</button>
      </div>
      <style>{`
        .lock-idle { animation: lockBreathe 3.2s ease-in-out infinite; }
        @keyframes lockBreathe { 0%, 100% { box-shadow: 0 8px 20px rgba(0,137,123,0.18), 0 0 0 0 rgba(0,137,123,0.22); } 50% { box-shadow: 0 8px 20px rgba(0,137,123,0.18), 0 0 0 9px rgba(0,137,123,0); } }
        .lock-scan .scan-line { animation: lockScan 1.1s ease-in-out infinite alternate; }
        @keyframes lockScan { from { transform: translateY(-9px); } to { transform: translateY(9px); } }
        @media (prefers-reduced-motion: reduce) { .lock-idle, .lock-scan .scan-line { animation: none; } }
      `}</style>
    </AuthShell>
  );
}

// Drawn here (teal lines): a face in a frame (Face ID) or a fingerprint
function FaceScanIcon() {
  return (
    <svg width="38" height="38" viewBox="0 0 40 40" fill="none" stroke="#00897B" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 12V8a4 4 0 0 1 4-4h4M28 4h4a4 4 0 0 1 4 4v4M36 28v4a4 4 0 0 1-4 4h-4M12 36H8a4 4 0 0 1-4-4v-4" />
      <path d="M14 15v3M26 15v3M20 15v7h-2M15 27c3 2.4 7 2.4 10 0" />
      <path className="scan-line" d="M8 20h24" stroke="#4DB6AC" strokeWidth="1.6" opacity="0.8" />
    </svg>
  );
}
function FingerprintIcon() {
  return (
    <svg width="38" height="38" viewBox="0 0 40 40" fill="none" stroke="#00897B" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
      <path d="M10 13a12 12 0 0 1 20 0M7 20c0-2 .4-3.8 1.2-5.5M33 22c0-2.6-.5-4.7-1.5-6.6" />
      <path d="M13 31c-1.5-2.8-2-5.6-2-9a9 9 0 0 1 18 0c0 2 0 3.6-.4 5.2" />
      <path d="M17 34c-1.6-3.2-2.4-7-2.4-12a5.4 5.4 0 0 1 10.8 0c0 4-.3 7.3-1.6 10.6" />
      <path d="M20 22c0 5 .6 9 2.4 12.5" />
      <path className="scan-line" d="M8 20h24" stroke="#4DB6AC" strokeWidth="1.6" opacity="0.8" />
    </svg>
  );
}

// 3.74.4: the passkey sign (a person and a key, the common passkey icon)
function PasskeyIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="7" r="4" /><path d="M3 21v-2a5 5 0 0 1 5-5h3" />
      <circle cx="18" cy="13" r="2.5" /><path d="M18 15.5V21l1.5-1.2L18 18.6l1.5-1.2" />
    </svg>
  );
}

// 3.72.0: Microsoft's four squares (their sign-in button guidelines)
function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" /><rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" /><rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
      <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
      <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18l2.67-2.07z"/>
      <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.31z"/>
    </svg>
  );
}

// The rest of the look is shared (components/AuthShell.jsx)
const S = {
  pwLabelRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' },
  unlockIcon: { width: 72, height: 72, margin: '0 auto 14px', borderRadius: 22, display: 'grid', placeItems: 'center', border: 'none', cursor: 'pointer', padding: 0, overflow: 'hidden',
    background: 'linear-gradient(135deg, #e0f2f1, #f1f8e9)', boxShadow: '0 8px 20px rgba(0,137,123,0.18), inset 0 0 0 1px rgba(0,137,123,0.15)' },
  badgeIcon: { width: 56, height: 56, margin: '0 auto 12px', borderRadius: 18, display: 'grid', placeItems: 'center', fontSize: 26,
    background: 'linear-gradient(135deg, #e0f2f1, #f1f8e9)', boxShadow: '0 8px 20px rgba(0,137,123,0.18), inset 0 0 0 1px rgba(0,137,123,0.15)' },
  codeDots: { display: 'flex', justifyContent: 'center', gap: 8, margin: '-4px 0 12px' },
  codeDot: { width: 8, height: 8, borderRadius: '50%', background: '#dfe7e6', transition: 'background .2s, transform .25s cubic-bezier(.34,1.6,.64,1)' },
  codeDotOn: { background: '#00897B', transform: 'scale(1.25)' },
  rememberRow: { display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: '#37474F', cursor: 'pointer', lineHeight: 1.45, textAlign: 'left', marginBottom: 10 },
  rememberHint: { display: 'block', fontSize: 12, color: '#78909C' },
  options: { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 8 },
  option: {
    display: 'flex', flexDirection: 'column', gap: 4, textAlign: 'left', padding: '13px 15px',
    border: '1.5px solid #dfe7e6', borderRadius: 14, background: '#fff', cursor: 'pointer', fontFamily: 'inherit'
  },
  optionHighlight: { border: '1.5px solid #00897B', background: '#f0faf8', boxShadow: '0 0 0 4px rgba(0,137,123,0.12)' },
  optionTitle: { fontSize: 14.5, fontWeight: 700, color: '#263238' },
  optionText: { fontSize: 13, color: '#607D8B', lineHeight: 1.45 },
  pickList: { border: 'none', margin: '0 0 10px', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 },
  pickRow: {
    display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 10, alignItems: 'center', padding: '11px 13px',
    border: '1.5px solid #dfe7e6', borderRadius: 14, cursor: 'pointer', background: '#fff'
  },
  pickRowOn: { border: '1.5px solid #00897B', background: '#f0faf8' },
  pickName: { fontSize: 14, fontWeight: 600, color: '#263238' },
  pickRole: { gridColumn: 2, fontSize: 12, color: '#607D8B' },
  srOnly: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' },
};
